/**
 * Extrai rótulos de botões/quick replies do payload URA (mesma lógica do simulador).
 */

import {
  parseChatbotButtonEntries,
  partitionChatbotButtonEntries,
  enrichSimulatorPaymentLinkButtons,
} from "./chatbotButtonLinks";

function listMessageToOptions(listMessage) {
  if (!listMessage) return [];
  if (Array.isArray(listMessage)) {
    return listMessage
      .map((x) => {
        if (typeof x === "string") {
          const title = x.trim();
          return title ? { id: "", title } : null;
        }
        if (x && typeof x === "object") {
          const title = String(x.title || x.nome || x.label || x.text || x.name || "").trim();
          const description = String(x.description || "").trim();
          if (!title && !description) return null;
          // Simulador/webchat: preferir description (nome completo) quando o title WA está truncado.
          const display =
            description && description.length > title.length ? description : title || description;
          return { id: String(x.id || x.rowId || "").trim(), title: display };
        }
        return null;
      })
      .filter(Boolean);
  }
  if (typeof listMessage === "object" && Array.isArray(listMessage.sections)) {
    const out = [];
    for (const sec of listMessage.sections) {
      const rows = Array.isArray(sec.rows) ? sec.rows : [];
      for (const r of rows) {
        const title = String(r?.title || r?.nome || r?.label || "").trim();
        const description = String(r?.description || "").trim();
        if (!title && !description) continue;
        const display =
          description && description.length > title.length ? description : title || description;
        out.push({ id: String(r?.id || r?.rowId || "").trim(), title: display });
      }
    }
    return out;
  }
  return [];
}

export function parseChatbotInteractiveButtons(payload) {
  const p = payload ?? {};
  const raw =
    p.buttonMessage ?? p.botoes ?? p.buttons ?? p.quickReplies ?? p.quick_replies ?? null;

  const entries = parseChatbotButtonEntries(raw);
  const { replies, links } = partitionChatbotButtonEntries(entries);

  let nextReplies = replies.map((entry) => ({
    label: entry.label,
    id: String(entry.id || entry.rawId || "").trim(),
  }));
  let nextLinks = links.map((entry) => ({ label: entry.label, url: entry.url }));

  if (!nextReplies.length && !nextLinks.length) {
    const fromList = listMessageToOptions(p.listMessage);
    if (fromList.length) {
      nextReplies = fromList.map((opt) => ({ label: opt.title, id: opt.id }));
    }
  }

  // Cidades URA: buttonMessage é CSV de rótulos; ids ##CIDADE## vêm em interactiveOptions.buttons.
  const ixButtons = Array.isArray(p.interactiveOptions?.buttons)
    ? p.interactiveOptions.buttons
    : [];
  if (ixButtons.length) {
    if (!nextReplies.length) {
      nextReplies = ixButtons
        .map((b) => ({
          label: String(b?.title || b?.label || "").trim(),
          id: String(b?.id || "").trim(),
        }))
        .filter((b) => b.label || b.id);
    } else {
      nextReplies = nextReplies.map((reply, idx) => {
        if (reply.id) return reply;
        const ix = ixButtons[idx];
        const ixId = String(ix?.id || "").trim();
        if (!ixId) return reply;
        return { ...reply, id: ixId };
      });
    }
  }

  const enriched = enrichSimulatorPaymentLinkButtons({
    text: p.text ?? p.mensagem ?? "",
    replies: nextReplies,
    links: nextLinks,
    interactiveOptions: p.interactiveOptions,
  });

  return {
    replies: enriched.replies,
    links: enriched.links,
    text: enriched.text,
  };
}

export function parseChatbotQuickReplies(payload) {
  const { replies } = parseChatbotInteractiveButtons(payload);
  return replies.map((entry) => entry.label);
}

/** Opções clicáveis com id (quando existir) + label — para Web Chat / URA. */
export function getChatbotInteractiveOptions(metadata) {
  if (!metadata || typeof metadata !== "object") return [];
  if (Array.isArray(metadata.buttons) && metadata.buttons.length) {
    return metadata.buttons
      .map((b) => {
        if (typeof b === "string") {
          const label = b.trim();
          return label ? { id: "", label } : null;
        }
        if (b && typeof b === "object") {
          const label = String(b.label || b.title || b.text || "").trim();
          if (!label) return null;
          return { id: String(b.id || "").trim(), label };
        }
        return null;
      })
      .filter(Boolean);
  }
  const { replies } = parseChatbotInteractiveButtons(metadata);
  return replies.map((r) => ({ id: r.id || "", label: r.label }));
}

export function getChatbotInteractiveLabels(metadata) {
  return getChatbotInteractiveOptions(metadata).map((o) => o.label);
}

export function getChatbotLinkButtons(metadata) {
  if (!metadata || typeof metadata !== "object") return [];
  if (Array.isArray(metadata.linkButtons) && metadata.linkButtons.length) {
    return metadata.linkButtons
      .map((item) => ({
        label: String(item?.label || item?.title || "Abrir link").trim(),
        url: String(item?.url || item?.href || "").trim(),
      }))
      .filter((item) => item.url);
  }

  const { links } = parseChatbotInteractiveButtons(metadata);
  return links;
}

// Re-export partition helper for components that need both types.
export { partitionChatbotButtonEntries as partitionChatbotButtons };
